import React, { createContext, useContext, useMemo, useState } from "react";
import { Box } from "@mui/material";
import LeftDrawer from "components/PageLayouts/Drawers/LeftDrawer";
import RightDrawer from "components/PageLayouts/Drawers/RightDrawer";
import BottomDrawer from "components/PageLayouts/Drawers/BottomDrawer";

export interface GeneralLayoutProps {
  useLeftDrawer?: boolean;
  useRightDrawer?: boolean;
  useBottomDrawer?: boolean;

  leftDrawerContent?: React.ReactNode;
  rightDrawerContent?: React.ReactNode;
  bottomDrawerContent?: React.ReactNode;

  leftDrawerWidth?: number;
  rightDrawerWidth?: number;
  bottomDrawerHeight?: number;

  leftDrawerWidthClosed?: number;
  rightDrawerWidthClosed?: number;
  bottomDrawerHeightClosed?: number;

  /** Drawer open/close defaults */
  defaultOpenLeft?: boolean;
  defaultOpenRight?: boolean;
  defaultOpenBottom?: boolean;

  /** Position offsets */
  topOffset?: number;
  bottomOffset?: number;
  leftOffset?: number;
  rightOffset?: number;

  /** Main content */
  children: React.ReactNode;

  /** Optional sx overrides */
  sx?: Record<string, any>;
}

interface PageLayoutContextValue {
  leftDrawerWidth: number;
  rightDrawerWidth: number;
  bottomDrawerHeight: number;
  leftDrawerWidthClosed: number;
  rightDrawerWidthClosed: number;
  bottomDrawerHeightClosed: number;
  useLeftDrawer: boolean;
  useRightDrawer: boolean;
  useBottomDrawer: boolean;
  openLeft: boolean;
  openRight: boolean;
  openBottom: boolean;
  toggleLeft: () => void;
  toggleRight: () => void;
  toggleBottom: () => void;
}

const PageLayoutContext = createContext<PageLayoutContextValue | null>(null);

export const usePageLayout = () => {
  const ctx = useContext(PageLayoutContext);
  if (!ctx) throw new Error("usePageLayout must be used within GeneralLayout");
  return ctx;
};

const GeneralLayout: React.FC<GeneralLayoutProps> = ({
  useLeftDrawer = false,
  useRightDrawer = false,
  useBottomDrawer = false,
  leftDrawerContent,
  rightDrawerContent,
  bottomDrawerContent,
  leftDrawerWidth = 250,
  rightDrawerWidth = 350,
  bottomDrawerHeight = 300,
  leftDrawerWidthClosed = 50,
  rightDrawerWidthClosed = 50,
  bottomDrawerHeightClosed = 50,
  defaultOpenLeft = false,
  defaultOpenRight = false,
  defaultOpenBottom = false,
  topOffset = 0,
  bottomOffset = 0,
  leftOffset = 0,
  rightOffset = 0,
  children,
  sx = {},
}) => {
  // Initial states based on props
  const [openLeft, setOpenLeft] = useState(defaultOpenLeft);
  const [openRight, setOpenRight] = useState(defaultOpenRight);
  const [openBottom, setOpenBottom] = useState(defaultOpenBottom);

  const toggleLeft = () => setOpenLeft((v) => !v);
  const toggleRight = () => setOpenRight((v) => !v);
  const toggleBottom = () => setOpenBottom((v) => !v);

  const contextValue = useMemo(
    () => ({
      leftDrawerWidth,
      rightDrawerWidth,
      bottomDrawerHeight,
      leftDrawerWidthClosed,
      rightDrawerWidthClosed,
      bottomDrawerHeightClosed,
      useLeftDrawer,
      useRightDrawer,
      useBottomDrawer,
      openLeft,
      openRight,
      openBottom,
      toggleLeft,
      toggleRight,
      toggleBottom,
    }),
    [
      leftDrawerWidth,
      rightDrawerWidth,
      bottomDrawerHeight,
      useLeftDrawer,
      useRightDrawer,
      useBottomDrawer,
      openLeft,
      openRight,
      openBottom,
    ]
  );

  const effectiveLeft = useLeftDrawer
    ? (openLeft ? leftDrawerWidth + leftOffset : leftOffset + leftDrawerWidthClosed)
    : leftOffset;

  const effectiveRight = useRightDrawer
    ? (openRight ? rightDrawerWidth + rightOffset : rightOffset + rightDrawerWidthClosed)
    : rightOffset;

  const effectiveBottom = useBottomDrawer
    ? (openBottom ? bottomDrawerHeight + bottomOffset : bottomOffset + bottomDrawerHeightClosed)
    : bottomOffset;

  return (
    <PageLayoutContext.Provider value={contextValue}>
      <Box
        sx={{
          position: "relative",
          width: "100%",
          height: "100vh",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          ...sx,
        }}
      >
        {/* MAIN CONTENT */}
        <Box
          sx={{
            position: "absolute",
            top: topOffset,
            left: effectiveLeft,
            right: effectiveRight,
            bottom: effectiveBottom,
            overflow: "auto",
            transition: "all 0.3s ease",
            backgroundColor: (theme) => theme.custom.base.background.default.dark,
          }}
        >
          {children}
        </Box>

        {/* DRAWERS */}
        {useLeftDrawer && (
          <LeftDrawer
            drawerContent={leftDrawerContent}
            width={leftDrawerWidth}
            topOffset={topOffset}
            bottomOffset={bottomOffset}
          />
        )}

        {useRightDrawer && (
          <RightDrawer
            drawerContent={rightDrawerContent}
            width={rightDrawerWidth}
            topOffset={topOffset}
            bottomOffset={bottomOffset}
          />
        )}

        {useBottomDrawer && (
          <BottomDrawer
            drawerContent={bottomDrawerContent}
            height={bottomDrawerHeight}
            leftOffset={leftOffset + (openLeft ? leftDrawerWidth : 0)}
            rightOffset={rightOffset + (openRight ? rightDrawerWidth : 0)}
          />
        )}
      </Box>
    </PageLayoutContext.Provider>
  );
};

export default GeneralLayout;
